import { GridLoader } from 'react-spinners';
import { motion } from 'framer-motion';

// Custom multi-color grid loader matching Skills Matrix logo
const MultiColorGridLoader = ({ size = 15, loading = true, className = "" }) => {
  if (!loading) return null;

  // Grid layout matching the logo:
  // Row 1: Green, Green, Green
  // Row 2: Light Teal, Orange, Light Teal
  // Row 3: Light Teal, Red, Yellow/Orange
  const colors = [
    '#00a551', '#00a551', '#00a551',     // Top row: all green
    '#d3f3f2', '#f05a28', '#d3f3f2',     // Middle row: teal, orange, teal
    '#d3f3f2', '#ee2c2f', '#ffa616'      // Bottom row: teal, red, yellow-orange
  ];
  const dotSize = size * 0.6;
  const gap = size * 0.4;

  return (
    <div className={`grid grid-cols-3 ${className}`} style={{ gap: `${gap}px` }}>
      {colors.map((color, index) => (
        index === 8 ? (
          // 4-pointed star for bottom right
          <motion.svg
            key={index}
            width={dotSize}
            height={dotSize}
            viewBox="0 0 100 100"
            animate={{
              scale: [1, 1.2, 1],
              filter: [
                'brightness(1) drop-shadow(0 0 0px rgba(255, 166, 22, 0))',
                'brightness(1.4) drop-shadow(0 0 8px rgba(255, 166, 22, 0.8))',
                'brightness(1) drop-shadow(0 0 0px rgba(255, 166, 22, 0))'
              ],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: index * 0.1,
              ease: "easeInOut"
            }}
          >
            <path
              d="M50,5 L62,38 L95,50 L62,62 L50,95 L38,62 L5,50 L38,38 Z"
              fill={color}
            />
          </motion.svg>
        ) : (
          <motion.div
            key={index}
            style={{
              width: `${dotSize}px`,
              height: `${dotSize}px`,
              backgroundColor: color,
              borderRadius: '25%',
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.5, 1],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: index * 0.1,
              ease: "easeInOut"
            }}
          />
        )
      ))}
    </div>
  );
};

export const LoadingSpinner = ({
  size = 15,
  color = "#00a551", // Skills Matrix green (used as fallback for single-color mode)
  loading = true,
  className = "",
  containerClassName = "",
  multiColor = true // New prop to toggle between multi-color and single-color
}) => {
  if (!loading) return null;

  return (
    <div className={`flex items-center justify-center ${containerClassName}`}>
      {multiColor ? (
        <MultiColorGridLoader size={size} loading={loading} className={className} />
      ) : (
        <GridLoader
          color={color}
          loading={loading}
          size={size}
          className={className}
          aria-label="Loading Spinner"
        />
      )}
    </div>
  );
};

export const FullPageLoader = ({ loading = true }) => {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner loading={loading} />
    </div>
  );
};

export const InlineLoader = ({ loading = true, size = 8 }) => {
  return (
    <LoadingSpinner
      loading={loading}
      size={size}
      containerClassName="inline-flex mx-2"
    />
  );
};